import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { api, ApiError, type PublicRankingPayload } from "../lib/api";
import { formatDate } from "../lib/dateFormat";

/** Página pública del ranking oficial (R2): sirve la ÚLTIMA PUBLICACIÓN
 * congelada — el mismo snapshot con el que se arman aceptación y siembra,
 * como exige el reglamento FMT ("el ranking publicado al día del cierre"). */
export default function PublicRankingPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") ?? undefined;
  const [payload, setPayload] = useState<PublicRankingPayload | null>(null);
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await api.rankings.publicRanking(slug, category);
      setPayload(resp.data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo cargar el ranking.");
    } finally {
      setLoading(false);
    }
  }, [slug, category]);

  useEffect(() => {
    void load();
  }, [load]);

  const normalizedQuery = query.trim().toLowerCase();
  const rows = (payload?.rows ?? []).filter(
    (row) => !normalizedQuery || row.display_name.toLowerCase().includes(normalizedQuery),
  );

  return (
    <section className="public-ranking-page">
      <div className="card public-ranking-card">
        <p className="eyebrow">Torneos123 · Ranking oficial</p>
        <h1>{payload?.system.name ?? "Ranking"}</h1>
        {payload?.published_at ? (
          <p className="muted">
            Publicación vigente: {formatDate(payload.published_at)} — con esta se arman las siembras.
          </p>
        ) : (
          <p className="muted">Aún no hay una publicación de este ranking.</p>
        )}

        {payload && payload.categories.length > 1 && (
          <div className="public-ranking-card__categories">
            {payload.categories.map((item) => (
              <button
                key={item.code}
                type="button"
                className={`btn btn--ghost${payload.category.code === item.code ? " is-active" : ""}`}
                onClick={() => setSearchParams({ category: item.code })}
              >
                {item.name}
              </button>
            ))}
          </div>
        )}

        <input
          className="public-ranking-card__search"
          placeholder="Buscar jugador…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />

        {loading ? (
          <p className="muted">Cargando ranking…</p>
        ) : error ? (
          <p className="form-error" role="alert">{error}</p>
        ) : rows.length === 0 ? (
          <p className="muted">
            {normalizedQuery
              ? "Ningún jugador coincide con la búsqueda."
              : "Sin posiciones publicadas todavía en esta categoría."}
          </p>
        ) : (
          <div className="public-ranking-card__table-wrap">
            <table className="public-ranking-card__table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Jugador</th>
                  <th>Puntos</th>
                  <th>Torneos</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.player_profile_id} className={row.position <= 3 ? "is-top" : undefined}>
                    <td>{row.position}</td>
                    <td>{row.display_name}</td>
                    <td>{row.points_total.toLocaleString("es-MX")}</td>
                    <td>{row.tournaments_counted}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
